import { useEffect, useRef, useState } from 'react'
import MDEditor from '@uiw/react-md-editor'
import '@uiw/react-md-editor/markdown-editor.css'
import '@uiw/react-markdown-preview/markdown.css'
import { UploadIcon } from './icons/UploadIcon'
import { EyeIcon } from './icons/EyeIcon'
import { PencilIcon } from './icons/PencilIcon'

export interface MdEditorProps {
  id: string
  value: string
  onChange: (value: string) => void
  filename: string
  placeholder?: string
  readOnly?: boolean
  /** Render in preview mode first, with edit opt-in via the pencil segment.
   * Prototype parity for the DNA columns (`dna-render-col`); off elsewhere, so a
   * form whose markdown field is the thing the user came to fill in (e.g. the
   * skill Definition) still opens ready to type. */
  previewFirst?: boolean
  height?: number
  maxUploadBytes?: number
  invalidTypeMessage: string
  tooLargeMessage?: string
  uploadTitle?: string
  previewTitle?: string
  editTitle?: string
  readOnlyTitle?: string
}

function useColorMode() {
  const [mode, setMode] = useState<'light' | 'dark'>(
    () => (document.documentElement.dataset['theme'] === 'dark' ? 'dark' : 'light')
  )

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setMode(document.documentElement.dataset['theme'] === 'dark' ? 'dark' : 'light')
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])

  return mode
}

export function MdEditor({
  id,
  value,
  onChange,
  filename,
  placeholder,
  readOnly = false,
  previewFirst = false,
  height = 200,
  maxUploadBytes,
  invalidTypeMessage,
  tooLargeMessage,
  uploadTitle,
  previewTitle,
  editTitle,
  readOnlyTitle,
}: Readonly<MdEditorProps>) {
  const [editing, setEditing] = useState(!readOnly && !previewFirst)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const colorMode = useColorMode()

  useEffect(() => {
    if (readOnly) setEditing(false)
  }, [readOnly])

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.endsWith('.md')) {
      setUploadError(invalidTypeMessage)
      e.target.value = ''
      return
    }
    if (maxUploadBytes && file.size > maxUploadBytes) {
      setUploadError(tooLargeMessage ?? invalidTypeMessage)
      e.target.value = ''
      return
    }
    setUploadError(null)
    onChange(await file.text())
    e.target.value = ''
  }

  return (
    <div className={`dna-render-col${editing ? ' editing' : ''}`} data-color-mode={colorMode}>
      <div className="dna-render-toolbar">
        <span className="dna-filename">{filename}</span>
        <div className="dna-toolbar-actions">
          <button
            type="button"
            className="dna-upload-btn"
            title={readOnly ? readOnlyTitle : uploadTitle}
            disabled={readOnly}
            style={readOnly ? { opacity: 0.35, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadIcon />
          </button>
          <div className="dna-mode-switch" role="tablist">
            <button
              type="button"
              className={`dna-mode-seg${!editing ? ' active' : ''}`}
              onClick={() => setEditing(false)}
              title={previewTitle}
              aria-label={previewTitle}
              role="tab"
              aria-selected={!editing}
            >
              <EyeIcon />
            </button>
            <button
              type="button"
              className={`dna-mode-seg${editing ? ' active' : ''}`}
              onClick={() => !readOnly && setEditing(true)}
              title={readOnly ? readOnlyTitle : editTitle}
              aria-label={editTitle}
              role="tab"
              aria-selected={editing}
              disabled={readOnly}
              style={readOnly ? { opacity: 0.35, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
            >
              <PencilIcon size={13} />
            </button>
          </div>
        </div>
      </div>
      {uploadError && (
        <p role="alert" style={{ color: 'var(--danger)', fontSize: 12, margin: '4px 0 0', padding: '0 12px' }}>{uploadError}</p>
      )}
      <MDEditor
        value={value}
        onChange={(v) => onChange(v ?? '')}
        height={height}
        preview={editing ? 'edit' : 'preview'}
        hideToolbar
        /* Prototype parity: `.dna-editor-textarea` is `resize: vertical`. The
           library's own dragbar covers both modes, so the preview grows too. */
        visibleDragbar
        textareaProps={{ id, placeholder, readOnly }}
        commands={[]}
        extraCommands={[]}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".md"
        style={{ display: 'none' }}
        aria-hidden="true"
        onChange={handleFileUpload}
      />
    </div>
  )
}
